import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Copy, Eye, Loader2, Megaphone, PauseCircle, ShoppingCart, UserPlus, Wallet } from "lucide-react";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import { API } from "@/App";
import { tokenStore } from "@/lib/token";

// Espace ambassadeur (/ambassadeur) : lien perso, statistiques (visites,
// inscriptions, ventes), commissions à recevoir et déjà versées.

const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const KIND = { purchase: "Achat", renewal: "Renouvellement", ai_credits: "Crédits IA" };
const STATUS = { pending: ["À recevoir", "text-amber-200 bg-amber-500/10"], paid: ["Versée", "text-emerald-200 bg-emerald-500/10"], cancelled: ["Annulée", "text-white/40 bg-white/5"] };

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <p className="text-xs text-white/50 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" />{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {sub != null && <p className="text-[11px] text-white/40 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function AmbassadorPage() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = "Espace ambassadeur · VantaPulse";
    axios.get(`${API}/ambassador/me`, { headers: { Authorization: `Bearer ${tokenStore.get()}` } })
      .then((r) => setData(r.data)).catch(() => toast.error("Chargement impossible"));
  }, []);

  const copy = () => navigator.clipboard?.writeText(data.link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="ambassador-page">
      <Navbar />
      <main className="relative z-[2] max-w-5xl mx-auto px-6 pt-28 pb-20">
        {!data ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div> : !data.is_ambassador ? (
          <div className="max-w-xl mx-auto text-center rounded-2xl border border-white/10 bg-white/[0.03] p-8" data-testid="ambassador-not-yet">
            <Megaphone className="w-8 h-8 text-sky-300 mx-auto" />
            <h1 className="text-2xl font-bold mt-4">Deviens ambassadeur Vanta Pulse</h1>
            <p className="text-white/60 mt-2">{data.application?.status === "pending" ? "Ta candidature est en cours d'examen : on te répond très vite." : "Partage nos liens, anime la communauté et touche une commission sur les ventes que tu apportes."}</p>
            {data.application?.status !== "pending" && <Link to="/recrutement" className="inline-block mt-6 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-violet-600 font-semibold">Postuler</Link>}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold tracking-[0.2em] text-sky-300">ESPACE AMBASSADEUR</p>
                <h1 className="text-3xl sm:text-4xl font-black mt-2">Merci de faire grandir Vanta Pulse 🚀</h1>
              </div>
              <p className="text-sm text-white/55">Commission : <strong className="text-white">{data.rate} %</strong> de chaque paiement de tes filleuls{data.months ? `, pendant ${data.months} mois après leur inscription` : ""}.</p>
            </div>
            {data.status === "paused" && <p className="mt-4 flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100"><PauseCircle className="w-4 h-4" />Ton compte ambassadeur est en pause : les nouvelles ventes ne donnent pas de commission pour le moment.</p>}

            <div className="mt-6 rounded-2xl border border-sky-400/25 bg-gradient-to-r from-blue-600/15 to-violet-600/15 p-5">
              <p className="text-sm text-white/70">Ton lien perso à partager partout</p>
              <div className="mt-3 flex flex-col sm:flex-row gap-3">
                <code className="flex-1 min-w-0 truncate rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-sky-200" data-testid="ambassador-link">{data.link}</code>
                <button type="button" onClick={copy} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white text-black font-semibold">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}{copied ? "Copié" : "Copier"}</button>
              </div>
              <p className="text-xs text-white/45 mt-3">Chaque personne qui s'inscrit après avoir cliqué sur ton lien devient ton filleul.</p>
            </div>

            <h2 className="text-lg font-semibold mt-8 mb-3">Tes résultats <span className="text-white/40 text-sm font-normal">(depuis le début · 30 derniers jours)</span></h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat icon={Eye} label="Visiteurs" value={data.stats.visits} sub={`${data.stats_30d.visits} sur 30 j`} />
              <Stat icon={UserPlus} label="Inscriptions" value={data.stats.signups} sub={`${data.stats_30d.signups} sur 30 j`} />
              <Stat icon={ShoppingCart} label="Ventes" value={data.stats.sales} sub={`${eur(data.stats.revenue)} au total`} />
              <Stat icon={Wallet} label="Commissions" value={eur(data.stats.pending)} sub={`à recevoir · ${eur(data.stats.paid)} déjà versées`} />
            </div>

            <h2 className="text-lg font-semibold mt-8 mb-3">Dernières commissions</h2>
            {data.commissions.length === 0 ? <p className="text-sm text-white/45">Pas encore de vente : partage ton lien, la première arrive vite 💪</p> : (
              <div className="rounded-2xl border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.04] text-white/50 text-xs"><tr><th className="text-left px-4 py-2.5">Date</th><th className="text-left px-4 py-2.5">Client</th><th className="text-left px-4 py-2.5">Type</th><th className="text-right px-4 py-2.5">Paiement</th><th className="text-right px-4 py-2.5">Commission</th><th className="text-right px-4 py-2.5">État</th></tr></thead>
                  <tbody>
                    {data.commissions.map((c) => (
                      <tr key={c.id} className="border-t border-white/5">
                        <td className="px-4 py-2.5 whitespace-nowrap">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                        <td className="px-4 py-2.5">{c.buyer}</td>
                        <td className="px-4 py-2.5 text-white/60">{KIND[c.kind] || c.kind}</td>
                        <td className="px-4 py-2.5 text-right">{eur(c.amount)}</td>
                        <td className="px-4 py-2.5 text-right font-semibold">{eur(c.commission)}</td>
                        <td className="px-4 py-2.5 text-right"><span className={`px-2 py-0.5 rounded-md text-xs ${STATUS[c.status]?.[1]}`}>{STATUS[c.status]?.[0]}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-white/40 mt-4">Les commissions sont versées par l'équipe (on te contacte sur le Discord). Un achat remboursé annule sa commission.</p>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
